import React from "react";
import { Link, useLocation } from "@tanstack/react-router";
import {
  BookOpenCheck,
  Bell,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  Handshake,
  LogOut,
  MessageSquareQuote,
  Menu,
  Moon,
  ReceiptText,
  ScanLine,
  Settings,
  ShieldCheck,
  Sun,
  UserRound,
  X,
} from "lucide-react";
import { useAuth } from "@core/auth/AuthProvider";
import { getModuleNavigation, orderModuleItems } from "@core/module-registry";
import { useOrganization } from "@core/organizations/OrganizationProvider";
import { useAppPreferences } from "@core/preferences/AppPreferencesProvider";

const iconMap = { Bell, BriefcaseBusiness, BookOpenCheck, CalendarDays, Handshake, MessageSquareQuote, ReceiptText, ScanLine, ShieldCheck };
const navigation = getModuleNavigation();

export function MobileNav() {
  const { user, signOut } = useAuth();
  const { organizations, activeOrganization, selectOrganization, can, isSuperadmin } = useOrganization();
  const { preferences, setPreference, isModuleVisible } = useAppPreferences();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const closeButtonRef = React.useRef(null);

  React.useEffect(() => setMenuOpen(false), [location.pathname]);

  React.useEffect(() => {
    if (!menuOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", closeOnEscape);
    closeButtonRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [menuOpen]);
  if (!preferences.mobileAppbarVisible) return null;

  const items = orderModuleItems(
    navigation.filter((item) => can(item.permission) && isModuleVisible(item.moduleId)),
    preferences.navigationOrder?.mobile,
  )
    .slice(0, preferences.mobileAppbarItemsCount);
  const showLabels = preferences.mobileAppbarDisplayMode !== "icon-only";
  const ThemeIcon = preferences.theme === "light" ? Sun : Moon;
  const displayName = preferences.preferredName || user.displayName || "Vendor user";

  return (
    <>
      {menuOpen && (
        <div className="vp-mobile-menu" role="dialog" aria-modal="true" aria-label="VenPro menu">
          <button className="vp-mobile-menu-scrim" type="button" onClick={() => setMenuOpen(false)} aria-label="Close menu" />
          <section className="vp-mobile-menu-sheet">
            <header>
              <div className="vp-avatar" aria-hidden="true">{displayName.slice(0, 1)}</div>
              <div>
                <strong>{displayName}</strong>
                <span>{isSuperadmin ? "VenPro superadmin" : (user.email || activeOrganization.name)}</span>
              </div>
              <button ref={closeButtonRef} type="button" className="vp-icon-control" onClick={() => setMenuOpen(false)} aria-label="Close menu"><X /></button>
            </header>
            <label className="vp-mobile-org-select">
              <Building2 aria-hidden="true" />
              <span>Active business</span>
              <select value={activeOrganization.id} onChange={(event) => selectOrganization(event.target.value)}>
                {organizations.map((organization) => (
                  <option key={organization.id} value={organization.id}>{organization.name}</option>
                ))}
              </select>
            </label>
            <nav>
              <Link to="/settings/profile"><UserRound /> Profile</Link>
              <Link to="/settings"><Settings /> Settings</Link>
              <button type="button" onClick={() => setPreference("theme", preferences.theme === "dark" ? "light" : "dark")}>
                <ThemeIcon /> Theme <span>{preferences.theme}</span>
              </button>
              <button type="button" onClick={signOut}><LogOut /> Sign out</button>
            </nav>
          </section>
        </div>
      )}
      <nav className={`vp-mobilebar vp-mobile-${preferences.mobileAppbarSize}`} aria-label="Mobile navigation">
        {items.map((item) => {
          const Icon = iconMap[item.icon] ?? BriefcaseBusiness;
          const active = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
          return (
            <Link key={item.id} to={item.href} className={active ? "is-active" : ""}>
              <Icon aria-hidden="true" />
              {showLabels && <span>{item.label}</span>}
            </Link>
          );
        })}
        <button type="button" onClick={() => setMenuOpen(true)} aria-expanded={menuOpen}>
          <Menu aria-hidden="true" />
          {showLabels && <span>Menu</span>}
        </button>
      </nav>
    </>
  );
}
